import React from 'react';
import '../../styles/AutomationCard.css';
import { Button, Input, Dropdown, ExitScreen, Switch, SelectionChip } from '../';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { HugeiconsIcon } from '@hugeicons/react';
import { BedDoubleIcon, Bathtub02Icon, PoolIcon, CarParking01Icon, Calendar03Icon } from '@hugeicons/core-free-icons';
import { colour, formatterLong } from '../../styles/GlobalStyles';

// Format price for display: thousands separators, no decimals. State stays digits-only.
const formatPriceForDisplay = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const str = String(val).replace(/,/g, '');
    const num = parseFloat(str);
    if (isNaN(num) || !isFinite(num)) return '';
    return formatterLong.format(Math.floor(num)).replace('.00', '').replace('$', '');
};

// Parse price input: digits only, so state and Firebase never get commas.
const parsePriceInput = (val) => String(val ?? '').replace(/\D/g, '');

// Supported counties for MLS automations (from backend)
const SUPPORTED_COUNTIES = [
    { id: '04013', name: 'Maricopa County, AZ' },
    { id: '12053', name: 'Hernando County, FL' },
    { id: '12057', name: 'Hillsborough County, FL' },
    { id: '12101', name: 'Pasco County, FL' },
    { id: '12103', name: 'Pinellas County, FL' },
    { id: '20091', name: 'Johnson County, KS' },
    { id: '29047', name: 'Clay County, MO' },
    { id: '29095', name: 'Jackson County, MO' },
    { id: '29510', name: 'St. Louis City, MO' },
    { id: '29189', name: 'St. Louis County, MO' },
    { id: '29183', name: 'St. Charles County, MO' },
];

// Property condition options for MLS (match backend / Figma)
const PROPERTY_CONDITION_OPTIONS = [
    'Recently Remodeled',
    'Well-Maintained – Contemporary',
    'Well-Maintained – Traditional',
    'Moderate Rehab',
    'Gut Rehab',
    'Modern Build'
];

export const getCountyName = (countyId) => {
    const county = SUPPORTED_COUNTIES.find(c => c.id === countyId);
    return county ? county.name : countyId || 'No county selected';
};

function MLSConfigForm(props) {
    const {
        formCountyId,
        setFormCountyId,
        formLimit,
        setFormLimit,
        formFilters,
        updateMlsFilter,
        handleTogglePropertyCondition,
        countyDropdownOpen,
        setCountyDropdownOpen,
        mlsSaving,
        onSave,
        saveDisabled,
        mlsPropertyCount,
        mlsPropertyCountLoading,
        // Scanner mode props
        scannerMode,
        setScannerMode,
        formCountyIds,
        setFormCountyIds,
        formTargetProfit,
        setFormTargetProfit,
        countyMultiDropdownOpen,
        setCountyMultiDropdownOpen,
    } = props;

    const isScanner = scannerMode === 'distressed_cash';

    return (
        <>
            <div className="automation-ingestion-mls-form">
                {/* Scanner mode toggle */}
                <div className="automation-ingestion-mls-section">
                    <span className="body-bold colour-primary">Mode</span>
                    <div className="automation-ingestion-mls-chips">
                        <SelectionChip
                            label="Standard"
                            selected={!isScanner}
                            disabled={false}
                            func={() => setScannerMode(null)}
                            index={0}
                            leftIcon={null}
                            rightIcon={null}
                            darkMode={false}
                        />
                        <SelectionChip
                            label="Distressed Seller Scanner"
                            selected={isScanner}
                            disabled={false}
                            func={() => setScannerMode('distressed_cash')}
                            index={1}
                            leftIcon={null}
                            rightIcon={null}
                            darkMode={false}
                        />
                    </div>
                    {isScanner && (
                        <span className="label-regular colour-secondary" style={{ marginTop: '4px' }}>
                            Algorithmically finds the most motivated sellers using price history, status churn, and distress signals. Top 25 properties are fully underwritten daily.
                        </span>
                    )}
                </div>
                <div className="automation-form-divider"></div>

                {/* County selection — single for standard, multi for scanner */}
                <div className="automation-ingestion-mls-section">
                    <span className="body-bold colour-primary">{isScanner ? 'Counties' : 'County'}</span>
                    {isScanner ? (
                        <>
                            {countyMultiDropdownOpen && <ExitScreen clickScreen={() => setCountyMultiDropdownOpen(false)} />}
                            <Dropdown
                                open={countyMultiDropdownOpen}
                                icon={null}
                                label={
                                    formCountyIds?.length > 0
                                        ? formCountyIds.map(id => {
                                            const c = SUPPORTED_COUNTIES.find(sc => sc.id === id);
                                            return c ? c.name.split(',')[0] : id;
                                        }).join(', ')
                                        : 'Select counties'
                                }
                                disabled={false}
                                options={SUPPORTED_COUNTIES.map(c => ({
                                    label: c.name,
                                    selected: (formCountyIds || []).includes(c.id)
                                }))}
                                multiselect={true}
                                toggleDropdown={() => setCountyMultiDropdownOpen(!countyMultiDropdownOpen)}
                                cta={(item, index) => {
                                    const countyId = SUPPORTED_COUNTIES[index].id;
                                    const current = formCountyIds || [];
                                    if (current.includes(countyId)) {
                                        setFormCountyIds(current.filter(id => id !== countyId));
                                    } else {
                                        setFormCountyIds([...current, countyId]);
                                    }
                                }}
                                sidealign="left"
                                topalign="bottom"
                            />
                        </>
                    ) : (
                        <>
                            {countyDropdownOpen && <ExitScreen clickScreen={() => setCountyDropdownOpen(false)} />}
                            <Dropdown
                                open={countyDropdownOpen}
                                icon={null}
                                label={getCountyName(formCountyId)}
                                disabled={false}
                                options={SUPPORTED_COUNTIES.map(c => ({ label: c.name, selected: c.id === formCountyId }))}
                                multiselect={false}
                                toggleDropdown={() => setCountyDropdownOpen(!countyDropdownOpen)}
                                cta={(item, index) => {
                                    setFormCountyId(SUPPORTED_COUNTIES[index].id);
                                    setCountyDropdownOpen(false);
                                }}
                                sidealign="left"
                                topalign="bottom"
                            />
                        </>
                    )}
                </div>

                {/* Max results — standard mode only */}
                {!isScanner && (
                    <div className="automation-ingestion-mls-section">
                        <Input
                            label="Max results per run"
                            labelType="default"
                            info={false}
                            value={formLimit}
                            onChange={(e) => setFormLimit(e.target.value)}
                            placeholder="50"
                            type="text"
                            leftUnit={null}
                            rightUnit={null}
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={false}
                            multiline={false}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={false}
                            submitPromptDisabled={false}
                            submitPrompt={null}
                            onHeightChange={null}
                        />
                    </div>
                )}
                <div className="automation-ingestion-mls-section">
                    <span className="body-bold colour-primary">Price range</span>
                    <div className="automation-ingestion-mls-row-two automation-ingestion-mls-price-row">
                        <Input
                            label=""
                            labelType={null}
                            info={false}
                            value={formatPriceForDisplay(formFilters.priceMin)}
                            onChange={(e) => updateMlsFilter('priceMin', parsePriceInput(e.target.value))}
                            placeholder="50,000"
                            type="text"
                            leftUnit="$"
                            rightUnit={null}
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={false}
                            multiline={false}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={false}
                            submitPromptDisabled={false}
                            submitPrompt={null}
                            onHeightChange={null}
                        />
                        <span className="body-regular colour-primary">–</span>
                        <Input
                            label=""
                            labelType={null}
                            info={false}
                            value={formatPriceForDisplay(formFilters.priceMax)}
                            onChange={(e) => updateMlsFilter('priceMax', parsePriceInput(e.target.value))}
                            placeholder="1,000,000"
                            type="text"
                            leftUnit="$"
                            rightUnit={null}
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={false}
                            multiline={false}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={false}
                            submitPromptDisabled={false}
                            submitPrompt={null}
                            onHeightChange={null}
                        />
                    </div>
                </div>
                <div className="automation-form-divider"></div>
                {isScanner && (
                    <div className="automation-ingestion-mls-section">
                        <Input
                            label="Target profit %"
                            labelType="default"
                            info={false}
                            value={formTargetProfit}
                            onChange={(e) => setFormTargetProfit(e.target.value)}
                            placeholder="10"
                            type="text"
                            leftUnit={null}
                            rightUnit="%"
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={false}
                            multiline={false}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={false}
                            submitPromptDisabled={false}
                            submitPrompt={null}
                            onHeightChange={null}
                        />
                    </div>
                )}
                <span className="body-bold colour-primary">Property details</span>
                <div className="automation-ingestion-mls-section">
                    <div className="automation-ingestion-mls-label-row">
                        <HugeiconsIcon icon={BedDoubleIcon} size={16} color={colour.gray06} />
                        <span className="body-bold colour-primary">Beds</span>
                    </div>
                    <div className="automation-ingestion-mls-row-two">
                        <Input label="" labelType={null} info={false} value={formFilters.bedsMin} onChange={(e) => updateMlsFilter('bedsMin', e.target.value)} placeholder="Min" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                        <Input label="" labelType={null} info={false} value={formFilters.bedsMax} onChange={(e) => updateMlsFilter('bedsMax', e.target.value)} placeholder="Max" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                    </div>
                </div>
                <div className="automation-ingestion-mls-section">
                    <div className="automation-ingestion-mls-label-row">
                        <HugeiconsIcon icon={Bathtub02Icon} size={16} color={colour.gray06} />
                        <span className="body-bold colour-primary">Baths</span>
                    </div>
                    <div className="automation-ingestion-mls-row-two">
                        <Input label="" labelType={null} info={false} value={formFilters.bathsMin} onChange={(e) => updateMlsFilter('bathsMin', e.target.value)} placeholder="Min" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                        <Input label="" labelType={null} info={false} value={formFilters.bathsMax} onChange={(e) => updateMlsFilter('bathsMax', e.target.value)} placeholder="Max" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                    </div>
                </div>
                <div className="automation-ingestion-mls-section">
                    <span className="body-bold colour-primary">Living square footage</span>
                    <div className="automation-ingestion-mls-row-two">
                        <Input label="" labelType={null} info={false} value={formFilters.sqftMin} onChange={(e) => updateMlsFilter('sqftMin', e.target.value)} placeholder="Min" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                        <Input label="" labelType={null} info={false} value={formFilters.sqftMax} onChange={(e) => updateMlsFilter('sqftMax', e.target.value)} placeholder="Max" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                    </div>
                </div>
                {!isScanner && (
                    <div className="automation-ingestion-mls-section">
                        <span className="body-bold colour-primary">Year built</span>
                        <div className="automation-ingestion-mls-row-two">
                            <Input label="" labelType={null} info={false} value={formFilters.yearBuiltMin} onChange={(e) => updateMlsFilter('yearBuiltMin', e.target.value)} placeholder="Min" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                            <Input label="" labelType={null} info={false} value={formFilters.yearBuiltMax} onChange={(e) => updateMlsFilter('yearBuiltMax', e.target.value)} placeholder="Max" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                        </div>
                    </div>
                )}
                {!isScanner && (
                    <>
                        <div className="automation-form-divider"></div>
                        <span className="body-bold colour-primary">Additional features</span>
                        <div className="automation-ingestion-mls-toggles">
                            <div className="automation-ingestion-mls-toggle-item">
                                <div className="automation-ingestion-mls-label-row">
                                    <HugeiconsIcon icon={CarParking01Icon} size={16} color={colour.gray06} />
                                    <span className="body-bold colour-primary">Garage</span>
                                </div>
                                <Switch val={formFilters.hasGarage} setVal={(v) => updateMlsFilter('hasGarage', v)} label="Property has a garage" disabled={false} />
                            </div>
                            <div className="automation-ingestion-mls-toggle-item">
                                <div className="automation-ingestion-mls-label-row">
                                    <HugeiconsIcon icon={PoolIcon} size={16} color={colour.gray06} />
                                    <span className="body-bold colour-primary">Pool</span>
                                </div>
                                <Switch val={formFilters.hasPool} setVal={(v) => updateMlsFilter('hasPool', v)} label="Property has a pool" disabled={false} />
                            </div>
                        </div>
                        <div className="automation-form-divider"></div>
                        <span className="body-bold colour-primary">Property condition</span>
                        <div className="automation-ingestion-mls-chips">
                            {PROPERTY_CONDITION_OPTIONS.map((condition, index) => (
                                <SelectionChip
                                    key={index}
                                    label={condition}
                                    selected={formFilters.propertyConditions.includes(condition)}
                                    disabled={false}
                                    func={() => handleTogglePropertyCondition(condition)}
                                    index={index}
                                    leftIcon={null}
                                    rightIcon={null}
                                    darkMode={false}
                                />
                            ))}
                        </div>
                        <div className="automation-form-divider"></div>
                        <span className="body-bold colour-primary">Market indicators</span>
                        <div className="automation-ingestion-mls-section">
                            <div className="automation-ingestion-mls-label-row">
                                <HugeiconsIcon icon={Calendar03Icon} size={16} color={colour.gray06} />
                                <span className="body-bold colour-primary">Days on market</span>
                            </div>
                            <div className="automation-ingestion-mls-row-two">
                                <Input label="" labelType={null} info={false} value={formFilters.daysOnMarketMin} onChange={(e) => updateMlsFilter('daysOnMarketMin', e.target.value)} placeholder="Min" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                                <Input label="" labelType={null} info={false} value={formFilters.daysOnMarketMax} onChange={(e) => updateMlsFilter('daysOnMarketMax', e.target.value)} placeholder="Max" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                            </div>
                        </div>
                        <div className="automation-ingestion-mls-section">
                            <Input label="Minimum price cuts" labelType="default" info={false} value={formFilters.priceChangesMin} onChange={(e) => updateMlsFilter('priceChangesMin', e.target.value)} placeholder="1" type="text" leftUnit={null} rightUnit={null} unitSwitch={false} leftUnitSelected={true} unitSwitchFunc={null} disabled={false} multiline={false} error={false} onMouseEnter={null} onMouseLeave={null} reset={false} resetFunc={null} resetPosition={null} focusMultiline={false} prompt={false} submitPromptDisabled={false} submitPrompt={null} onHeightChange={null} />
                        </div>
                    </>
                )}
                <div className="automation-form-footer">
                    <div className="automation-form-footer-count">
                        {isScanner ? (
                            <span className="body-regular colour-secondary">
                                {formCountyIds?.length > 0
                                    ? `${formCountyIds.length} ${formCountyIds.length === 1 ? 'county' : 'counties'} selected · Top 25 daily`
                                    : 'Select at least one county'}
                            </span>
                        ) : (
                            <>
                                {!formCountyId?.trim() && (
                                    <span className="body-regular colour-secondary">Select a county</span>
                                )}
                                {formCountyId?.trim() && mlsPropertyCountLoading && (
                                    <span className="automation-form-footer-count-loading">
                                        <span className="automation-form-footer-count-lottie">
                                            <DotLottieReact
                                                src="https://lottie.host/7f215b0b-e72e-41ed-b9af-4249c3810a00/afvrLLR4JB.lottie"
                                                loop
                                                autoplay
                                            />
                                        </span>
                                        <span className="body-regular colour-secondary">Checking…</span>
                                    </span>
                                )}
                                {formCountyId?.trim() && !mlsPropertyCountLoading && mlsPropertyCount !== null && (
                                    <span className="body-regular colour-secondary">{mlsPropertyCount} properties match</span>
                                )}
                                {formCountyId?.trim() && !mlsPropertyCountLoading && mlsPropertyCount === null && (
                                    <span className="body-regular colour-secondary">—</span>
                                )}
                            </>
                        )}
                    </div>
                    <div className="automation-form-footer-button">
                        <Button
                            buttonType="primary-button"
                            size="large"
                            text="Save changes"
                            cta={onSave}
                            disabled={mlsSaving || saveDisabled}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={mlsSaving}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default MLSConfigForm;
